import { DependencyList, useEffect, useRef } from 'react';
import type { ActivationDefaults } from 'flows/tvi/hooks/use-get-estimated-activation-date';
import { Reservation } from 'flows/tvi/generic-buyflow/contexts';

interface DeriveFieldsArgs {
  reservation: unknown;
  activationDefaults: ActivationDefaults;
}

interface UseReservationFormSyncArgs<TFormData> {
  /** Current form-store status. Sync only runs when this is 'loaded'. */
  status: string;
  /** Reservation from BuyFlowContext (or provider-specific source). */
  reservation: Reservation;
  /** Result of useGetEstimatedActivationDate. */
  activationDefaults: ActivationDefaults;
  /**
   * Shallow-replace updater from the provider's form store. Use
   * replaceFormDataFields (not setFormData) so that passing a field as
   * undefined clears it instead of being silently dropped by mergeWith.
   */
  replaceFormDataFields: (updates: Partial<TFormData>) => void;
  /**
   * Provider-specific mapping from reservation + activation defaults to the
   * fields that should be written to the form store. Called when the sync
   * fires; may close over provider-local state, but any such state must be
   * listed in extraDeps to ensure the effect re-runs when it changes.
   */
  deriveFields: (args: DeriveFieldsArgs) => Partial<TFormData>;
  /**
   * Extra dependencies beyond the standard ones (status, reservation,
   * activationDefaults primitives). Used for provider-specific derived values
   * such as Xfinity's isTransferReservation.
   */
  extraDeps?: DependencyList;
  /**
   * Optional additional loading flag. When true, the sync is skipped. Use for
   * provider-specific data sources that feed into deriveFields, so the sync
   * waits until those are settled.
   */
  extraLoading?: boolean;
}

/**
 * Post-initialization sync for form-store fields that are derived from the
 * reservation's offer options. Fires when the reservation or activation
 * defaults change after the store is already loaded — addresses the case
 * where getOfferOptions returns dynamic options that differ from the base
 * reservation's defaults and the form store has already been initialized.
 */
export function useReservationFormSync<TFormData>({
  status,
  reservation,
  activationDefaults,
  replaceFormDataFields,
  deriveFields,
  extraDeps = [],
  extraLoading = false,
}: UseReservationFormSyncArgs<TFormData>): void {
  const deriveFieldsRef = useRef(deriveFields);
  deriveFieldsRef.current = deriveFields;

  const { loading, estimatedDate, defaultTimeWindow } = activationDefaults;

  useEffect(() => {
    if (status !== 'loaded' || !reservation || loading || extraLoading) {
      return;
    }
    replaceFormDataFields(
      deriveFieldsRef.current({ reservation, activationDefaults })
    );
    // activationDefaults is destructured into primitives in the dep array
    // below; deriveFields identity is captured via ref so a new closure each
    // render does not re-fire the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    status,
    reservation,
    loading,
    extraLoading,
    estimatedDate,
    defaultTimeWindow,
    replaceFormDataFields,
    ...extraDeps,
  ]);
}
